import type { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { QueryStatus } from "@reduxjs/toolkit/query";

type QueryEntry<T> = { status: QueryStatus; data?: T; error?: unknown };

const SETTLE_POLL_MS = 50;
const SETTLE_LIMIT_MS = 30_000;

// RTK Query 1.9: a forced initiate during a tag-invalidation refetch resolves at once with the old data.
export const untilSettled = async <T>(
  first: QueryEntry<T>,
  read: () => QueryEntry<T>,
  wait: () => Promise<void> = () => new Promise((resolve) => setTimeout(resolve, SETTLE_POLL_MS)),
  limitMs: number = SETTLE_LIMIT_MS,
): Promise<T> => {
  let entry = first;
  let waited = 0;
  while (entry.status === QueryStatus.pending && waited < limitMs) {
    await wait();
    waited += SETTLE_POLL_MS;
    entry = read();
  }
  if (entry.status === QueryStatus.pending) throw Error("The query did not settle in time");
  if (entry.status === QueryStatus.rejected) throw entry.error;
  if (!entry.data) throw Error("The query settled without data");
  return entry.data;
};

type Request<D> = Promise<QueryEntry<D>> & { unsubscribe: () => void };
type SettledEndpoint<A, D> = {
  select: (args: A) => (state: never) => QueryEntry<D>;
  initiate: (args: A, options: { forceRefetch: true }) => (dispatch: never, getState: never, extra: never) => Request<D>;
};
type ThunkApi = { dispatch: ThunkDispatch<unknown, unknown, AnyAction>; getState: () => unknown };

export const readSettled = async <A, D>(
  endpoint: SettledEndpoint<A, D>,
  args: A,
  { dispatch, getState }: ThunkApi,
): Promise<D> => {
  const read = endpoint.select(args);
  const request = dispatch(endpoint.initiate(args, { forceRefetch: true }) as never) as unknown as Request<D>;
  try {
    return await untilSettled(await request, () => read(getState() as never));
  } finally {
    request.unsubscribe();
  }
};
